import { useState } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const hpField = form.querySelector('.form-hp-field input') as HTMLInputElement;
    if (hpField && hpField.value.trim() !== '') {
      setSubmitStatus('success');
      return;
    }
    setSubmitStatus('loading');
    try {
      const formData = new URLSearchParams();
      formData.append('email', email);
      await fetch('https://readdy.ai/api/form/d917698bhec1he3oppqg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });
      setSubmitStatus('success');
      setEmail('');
    } catch {
      setSubmitStatus('error');
    }
  };

  return (
    <footer className="bg-foreground-900 text-background-100">
      <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-14 md:py-16">
        {/* Top: Brand + Description */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-8 mb-12">
          <div className="max-w-sm">
            <span className="font-heading text-xl font-bold tracking-wider text-background-50">
              PANDABUY <span className="font-light italic">Sheet</span>
            </span>
            <p className="text-foreground-300 text-sm mt-3 leading-relaxed">
              The ultimate pandabuy spreadsheet discovery hub — curated, structured, and updated daily for fashion enthusiasts worldwide.
            </p>
          </div>
          <div className="text-foreground-400 text-sm leading-relaxed max-w-md">
            PandabuySpreadsheet organizes thousands of curated items into structured spreadsheets with price comparisons, quality ratings, and weekly updates — helping you discover the best fashion finds faster.
          </div>
        </div>

        {/* Middle: Link Columns */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
          {/* Quick Links */}
          <div>
            <h4 className="text-background-50 text-sm font-semibold mb-4 whitespace-nowrap">Quick Links</h4>
            <div className="flex flex-col gap-2.5">
              <Link to="/" className="text-foreground-400 text-sm hover:text-background-50 transition-colors cursor-pointer whitespace-nowrap">Home</Link>
              <Link to="/article/pandabuy-spreadsheet-guide" className="text-foreground-400 text-sm hover:text-background-50 transition-colors cursor-pointer whitespace-nowrap">SEO Guide</Link>
              <Link to="/#categories" className="text-foreground-400 text-sm hover:text-background-50 transition-colors cursor-pointer whitespace-nowrap">Categories</Link>
              <Link to="/#faq" className="text-foreground-400 text-sm hover:text-background-50 transition-colors cursor-pointer whitespace-nowrap">FAQ</Link>
            </div>
          </div>

          {/* Top Categories */}
          <div>
            <h4 className="text-background-50 text-sm font-semibold mb-4 whitespace-nowrap">Top Categories</h4>
            <div className="flex flex-col gap-2.5">
              {categories.slice(0, 5).map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-foreground-400 text-sm hover:text-background-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {cat.name}
                </a>
              ))}
            </div>
          </div>

          {/* More Categories */}
          <div>
            <h4 className="text-background-50 text-sm font-semibold mb-4 whitespace-nowrap">More Categories</h4>
            <div className="flex flex-col gap-2.5">
              {categories.slice(5, 10).map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-foreground-400 text-sm hover:text-background-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {cat.name}
                </a>
              ))}
            </div>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="text-background-50 text-sm font-semibold mb-4 whitespace-nowrap">Stay Updated</h4>
            <p className="text-foreground-400 text-xs mb-3 leading-relaxed">
              Get notified when new spreadsheet categories drop.
            </p>
            <form
              onSubmit={handleNewsletterSubmit}
              data-readdy-form
              className="flex flex-col gap-2"
            >
              <div className="form-hp-field">
                <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" />
              </div>
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                className="w-full px-3 py-2.5 rounded-lg bg-foreground-800 text-background-50 text-sm border border-foreground-700 focus:outline-none focus:border-primary-400 placeholder:text-foreground-500"
              />
              <button
                type="submit"
                disabled={submitStatus === 'loading' || submitStatus === 'success'}
                className="w-full px-4 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 disabled:opacity-60 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                {submitStatus === 'loading' ? 'Subscribing...' : submitStatus === 'success' ? 'Subscribed!' : 'Subscribe'}
                <i className={`${submitStatus === 'success' ? 'ri-check-line' : 'ri-arrow-right-line'} text-xs`}></i>
              </button>
              {submitStatus === 'error' && (
                <p className="text-accent-400 text-xs">Something went wrong. Please try again.</p>
              )}
            </form>
          </div>
        </div>

        {/* Bottom */}
        <div className="border-t border-foreground-800 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-foreground-500 text-xs">
            <span>&copy; 2026 PandabuySpreadsheet Hub.</span>
            <span>All rights reserved.</span>
          </div>
          <div className="flex items-center gap-4">
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-foreground-500 text-xs hover:text-background-50 transition-colors cursor-pointer whitespace-nowrap">
              Main Site
            </a>
            <Link to="/" className="text-foreground-500 text-xs hover:text-background-50 transition-colors cursor-pointer whitespace-nowrap">
              Home
            </Link>
            <Link to="/article/pandabuy-spreadsheet-guide" className="text-foreground-500 text-xs hover:text-background-50 transition-colors cursor-pointer whitespace-nowrap">
              SEO Guide
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}